import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../../../../services/axios";
import { usePermissions } from "../../../../auth/PermissionProvider";
import { Permissions } from "../../../../auth/permissions";
import { DesfazerImportacao, ImportacaoResumo } from "../../../../interfaces/Financial";

interface Props {
  /** Qualquer valor que mude quando a lista de lançamentos mudou (importação, baixa): recarrega as importações. */
  versao: unknown;
  onDesfeita: () => void | Promise<void>;
}

const fmtMinuto = (iso: string) => {
  const d = new Date(iso.endsWith("Z") ? iso : `${iso}Z`);
  return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const fonteNome: Record<string, string> = { ofx: "OFX", pluggy: "Pluggy" };

const btnGhost =
  "rounded-xl border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 transition-all hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dv-blue-500 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:text-gray-200 dark:hover:bg-white/5";
const btnDanger =
  "rounded-xl bg-red-600 px-3 py-1.5 text-xs font-semibold text-white transition-all hover:bg-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 disabled:cursor-not-allowed disabled:opacity-60";

const chave = (i: ImportacaoResumo) => `${i.banco}|${i.fonte}|${i.dtImportado}`;

/**
 * Importações recentes do extrato com "Desfazer" (só `financial.admin`, só OFX): apaga o que ainda não foi
 * conciliado daquela importação. Pede uma segunda confirmação inline, sem diálogo do navegador.
 */
const ImportacoesRecentes: React.FC<Props> = ({ versao, onDesfeita }) => {
  const { can } = usePermissions();
  const admin = can(Permissions.FinancialAdmin);
  const [importacoes, setImportacoes] = useState<ImportacaoResumo[]>([]);
  const [confirmando, setConfirmando] = useState<string | null>(null);
  const [desfazendo, setDesfazendo] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    try {
      const { data } = await api.get<ImportacaoResumo[]>("/Financial/Extrato/Importacoes");
      setImportacoes(data);
    } catch {
      setImportacoes([]);
    }
  }, []);

  useEffect(() => {
    if (admin) carregar();
  }, [admin, carregar, versao]);

  const desfazer = async (i: ImportacaoResumo) => {
    const k = chave(i);
    setDesfazendo(k);
    try {
      const { data } = await api.delete<DesfazerImportacao>("/Financial/Extrato/Importacoes", {
        params: { banco: i.banco, fonte: i.fonte, dtImportado: i.dtImportado },
      });
      toast.success(
        `${data.removidos} lançamentos apagados` + (data.mantidos > 0 ? ` · ${data.mantidos} já conciliados ficaram` : ""),
        { position: "top-right", autoClose: 4000 }
      );
      setConfirmando(null);
      await carregar();
      await onDesfeita();
    } catch (error: any) {
      const msg = error?.response?.data;
      toast.error(typeof msg === "string" && msg ? msg : "Falha ao desfazer a importação.", { position: "top-right", autoClose: 4000 });
    } finally {
      setDesfazendo(null);
    }
  };

  // Só aparece para quem pode desfazer e quando há importação OFX pendente de algo: a lista vazia não ocupa a tela.
  const visiveis = importacoes.filter((i) => i.fonte === "ofx" && i.quantidade - i.conciliados > 0);
  if (!admin || visiveis.length === 0) return null;

  return (
    <div className="mt-4 rounded-xl border border-gray-200 p-4 dark:border-white/10">
      <h3 className="text-sm font-bold text-navy-700 dark:text-white">Importações recentes</h3>
      <p className="text-xs text-gray-600 dark:text-gray-400">
        Desfazer apaga os lançamentos daquela importação que ainda não foram conciliados. O que já virou baixa fica.
      </p>
      <ul className="mt-3 flex flex-col gap-2">
        {visiveis.map((i) => {
          const k = chave(i);
          const ocupado = desfazendo !== null;
          return (
            <li key={k} className="flex flex-col gap-2 text-sm sm:flex-row sm:items-center sm:justify-between">
              <span className="text-navy-700 dark:text-white">
                {fonteNome[i.fonte] ?? i.fonte} · {i.banco} · {fmtMinuto(i.dtImportado)} · {i.quantidade} lançamentos
                <span className="text-gray-500 dark:text-gray-400">
                  {" "}({i.naoIdentificados} a identificar, {i.conciliados} conciliados, {i.ignorados} ignorados)
                </span>
              </span>
              {confirmando === k ? (
                <span className="flex items-center gap-2">
                  <button type="button" className={btnDanger} disabled={ocupado} onClick={() => desfazer(i)}>
                    {desfazendo === k ? "Apagando..." : `Confirmar: apagar ${i.quantidade - i.conciliados}`}
                  </button>
                  <button type="button" className={btnGhost} disabled={ocupado} onClick={() => setConfirmando(null)}>
                    Cancelar
                  </button>
                </span>
              ) : (
                <button type="button" className={btnGhost} disabled={ocupado} onClick={() => setConfirmando(k)}>
                  Desfazer
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default ImportacoesRecentes;
